import { dropRight, last, sortBy } from 'lodash';

import { createCanvas } from 'lib/utils/canvas';
import { laplacianFilter } from 'verify/lib/utils/image';
import { BLANK_CAPTURE_BYTE_SIZE } from 'verify/lib/utils/webcam';

interface LivePhotoImageFrame {
  clarityScore: number;
  dateRecorded: number;
  dataUri: string;
}

export const FRAME_INTERVAL = 300;
export const IMAGE_FORMAT = 'image/jpeg';
export const IMAGE_QUALITY = 0.95;
const FRAME_BUFFER_SIZE = 6;
const HIGH_CLARITY_FRAME_COUNT = 2;

/**
 * Responsible for storing images as data URIs during autocapture.
 * Stores a frame every FRAME_INTERVAL, up to FRAME_BUFFER_SIZE frames.
 * On submit, the last frame and 2 highest clarity frames are submitted.
 * Note that the data URIs can take a lot of memory.
 */
export default class LivePhotoCaptureClient {
  private canvasBuffer: HTMLCanvasElement;
  private blankCanvasDataUri = '';
  private capturedFrames: Array<LivePhotoImageFrame> = [];
  /**
   * A function that returns a clarity score, with 0 being the lowest.
   */
  private filter: (canvasBuffer: HTMLCanvasElement) => number = laplacianFilter;
  private frameBufferSize: number = FRAME_BUFFER_SIZE;

  constructor({
    canvasBuffer,
    filter,
    frameBufferSize,
    skipBlankCaptureCheck,
  }: {
    canvasBuffer: HTMLCanvasElement;
    filter?: (canvasBuffer: HTMLCanvasElement) => number;
    frameBufferSize?: number;
    /**
     * For specs only. The blank capture check doesn't work in specs because
     * the blank canvas buffer will receive the same mock implementations as
     * the real canvas buffer.
     */
    skipBlankCaptureCheck?: boolean;
  }) {
    this.canvasBuffer = canvasBuffer;
    this.filter = filter ?? laplacianFilter;
    this.frameBufferSize = frameBufferSize ?? FRAME_BUFFER_SIZE;

    if (!skipBlankCaptureCheck) {
      // For detecting blank images
      const [blankCanvasBuffer, cleanupBlankCanvasBuffer] = createCanvas();
      blankCanvasBuffer.height = canvasBuffer.height;
      blankCanvasBuffer.width = canvasBuffer.width;
      this.blankCanvasDataUri = blankCanvasBuffer.toDataURL(IMAGE_FORMAT, IMAGE_QUALITY);
      cleanupBlankCanvasBuffer();
    }
  }

  /**
   * Reads an image off of the canvas buffer being drawn to by the webcam.
   */
  public capture = () => {
    const dataUri = this.canvasBuffer.toDataURL(IMAGE_FORMAT, IMAGE_QUALITY);

    // Try to avoid black screen captures. Based on prior logging, this only affects Apple devices.
    // We only need to check length because if the data URIs match, the length will be the same.
    // If the length is the same but the URIs don't match, it's almost certainly invalid anyways.
    if (dataUri.length <= this.blankCanvasDataUri.length) return;

    this.captureFrame({
      clarityScore: this.filter(this.canvasBuffer),
      dateRecorded: new Date().getTime(),
      dataUri,
    });
  };

  /**
   * Forces a frame to get pushed. Currently only used in BarcodeScanner.
   */
  public manualCapture = (dataUri: string) => {
    this.captureFrame({
      // Ensure manually captured frame is used.
      clarityScore: Number.MAX_SAFE_INTEGER,
      dateRecorded: new Date().getTime(),
      dataUri,
    });
  };

  public hasCaptures() {
    return this.capturedFrames.length > 0;
  }

  public captureCount() {
    return this.capturedFrames.length;
  }

  /**
   * Given this.frameBufferSize images, select the last image and the HIGH_CLARITY_FRAME_COUNT least blurry images.
   * We always keep the last image because the last image has the highest chance of being a processable photo.
   */
  public leastBlurryImages(): string[] {
    const lastFrame = last(this.capturedFrames);
    const allButLastFrame = dropRight(this.capturedFrames, 1);

    const framesSortedByClarity = sortBy(
      allButLastFrame,
      (frame) => (frame?.clarityScore ?? 0) * -1,
    );

    const highestClarityFramesInOriginalOrder = sortBy(
      framesSortedByClarity.slice(0, HIGH_CLARITY_FRAME_COUNT),
      [(frame) => frame.dateRecorded, (frame) => this.capturedFrames.indexOf(frame)],
    );

    if (lastFrame != null) {
      // Handle case where capturedFrames is empty for some reason
      highestClarityFramesInOriginalOrder.push(lastFrame);
    }

    return highestClarityFramesInOriginalOrder.map((frame) => frame.dataUri);
  }

  /**
   * @param asyncProcessFunction the async function to run on the last frame
   *
   * @returns the last captured frame
   */
  public async processLastFrame(
    asyncProcessFunction: (dataUri: string) => Promise<unknown>,
  ): Promise<unknown> {
    const lastFrame = last(this.capturedFrames);
    if (!lastFrame) {
      return undefined;
    }
    return await asyncProcessFunction(lastFrame.dataUri);
  }

  /**
   * Appends the given frame to the captured frame buffer, and ejects the oldest image.
   *
   * Attempts to ignore potential blank screens.
   *
   * TODO: currently still forces capture if we have less than the necessary
   * amount of images in the buffer, to avoid crashes / attempts to submit zero
   * images. This isn't ideal and should be handled downstream.
   */
  private captureFrame(frame: LivePhotoImageFrame) {
    if (
      this.capturedFrames.length > HIGH_CLARITY_FRAME_COUNT &&
      frame.dataUri.length < BLANK_CAPTURE_BYTE_SIZE &&
      frame.clarityScore === 0
    ) {
      // Try to avoid black screen captures
      return;
    }

    this.capturedFrames.push(frame);

    if (this.capturedFrames.length > this.frameBufferSize) {
      this.capturedFrames.shift();
    }
  }
}
